import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/pro-light-svg-icons';

import UIContext from 'components/Utils/UIContext';

import './BranchSortList.sass';

type BranchSortListProps = {
	// Arêtes VISIBLES dans l'ordre affiché (les arêtes masquées dans le scénario
	// ne sont pas listées mais conservent leur place — le parent réordonne sur
	// l'ensemble complet).
	branches: any[];
	// Réordonnancement : on insère l'arête `draggedId` avant/après `targetId`.
	// Le parent traduit ça en positions sur l'ensemble complet des arêtes.
	onReorder: (draggedId: string, targetId: string, after: boolean) => void;
};

/**
 * Liste verticale des arêtes du fishbone avec poignées, réordonnable en
 * drag-and-drop (HTML5 natif). Sert de contrôle du tri manuel dans le side
 * menu (le DnD sur le chart est désactivé). Pierre.
 */
export const BranchSortList = ({ branches = [], onReorder }: BranchSortListProps) => {
	const { t } = useTranslation();
	const uiContext = React.useContext(UIContext);
	const canEdit = uiContext.canEdit;
	const [dragId, setDragId] = React.useState<string | null>(null);
	const [over, setOver] = React.useState<{ id: string; after: boolean } | null>(null);

	// Ordre local OPTIMISTE : la persistance des positions se fait une par une
	// (chaque update re-render), ce qui ferait apparaître le tri en plusieurs
	// étapes. On applique donc l'ordre final immédiatement au drop et on ne
	// resynchronise sur les props que quand l'ensemble des arêtes change
	// (ajout/suppression), pas à chaque réordonnancement.
	const [localOrder, setLocalOrder] = React.useState<string[]>(branches.map((b) => b.id));
	const idsKey = [...branches.map((b) => b.id)].sort().join(',');
	React.useEffect(() => {
		setLocalOrder(branches.map((b) => b.id));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [idsKey]);

	const byId = new Map(branches.map((b) => [b.id, b]));
	const displayed = localOrder.map((id) => byId.get(id)).filter(Boolean) as any[];

	const handleDrop = (targetId: string) => {
		if (!dragId || dragId === targetId) {
			setDragId(null);
			setOver(null);
			return;
		}
		const after = over?.id === targetId ? over.after : false;
		const withoutDragged = displayed.filter((b) => b.id !== dragId);
		let insertIndex = withoutDragged.findIndex((b) => b.id === targetId);
		if (insertIndex < 0) {
			setDragId(null);
			setOver(null);
			return;
		}
		if (after) insertIndex += 1;
		// Ordre local appliqué immédiatement (optimiste, sur la liste visible).
		const newOrder = [...withoutDragged.map((b) => b.id)];
		newOrder.splice(insertIndex, 0, dragId);
		setLocalOrder(newOrder);
		// Persistance : le parent réordonne sur l'ensemble complet (les arêtes
		// masquées gardent leur place).
		onReorder(dragId, targetId, after);
		setDragId(null);
		setOver(null);
	};

	return (
		<div className="BranchSortList">
			{displayed.map((b) => {
				const isDragging = dragId === b.id;
				const showBefore = over?.id === b.id && !over.after;
				const showAfter = over?.id === b.id && over.after;
				return (
					<div
						key={b.id}
						className={
							'branch-row' +
							(isDragging ? ' dragging' : '') +
							(showBefore ? ' drop-before' : '') +
							(showAfter ? ' drop-after' : '')
						}
						draggable={canEdit}
						onDragStart={canEdit ? (e) => {
							setDragId(b.id);
							e.dataTransfer.effectAllowed = 'move';
						} : undefined}
						onDragOver={canEdit ? (e) => {
							e.preventDefault();
							const rect = e.currentTarget.getBoundingClientRect();
							const after = e.clientY > rect.top + rect.height / 2;
							if (over?.id !== b.id || over.after !== after) {
								setOver({ id: b.id, after });
							}
						} : undefined}
						onDrop={canEdit ? (e) => {
							e.preventDefault();
							handleDrop(b.id);
						} : undefined}
						onDragEnd={canEdit ? () => {
							setDragId(null);
							setOver(null);
						} : undefined}
					>
						<FontAwesomeIcon className="handle" icon={faGripVertical} />
						<span className="label" title={b.name}>
							{b.name || t('models.fishbone_branch.name')}
						</span>
					</div>
				);
			})}
		</div>
	);
};
